import React, { useEffect } from "react"
import { useSelector } from "react-redux"
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom"
import styled from "styled-components"

import "./App.css"

import Analytics from "Analytics"
import PanelLayout from "components/layout/PanelLayout"
import AdminOfferInfo from "pages/Admin/Panel/OfferInfo"
import AdminOffers from "pages/Admin/Panel/Offers"
import AdminProfiles from "pages/Admin/Panel/Profiles"
import AdminTools from "pages/Admin/Panel/Tools"
import AdminTransactions from "pages/Admin/Panel/Transactions"
import AdminTslNewsletterDashboard from "pages/Admin/Panel/TslNewsletterDashboard"
import AdminTslNewsletterPreview from "pages/Admin/Panel/TslNewsletterPreview"
import ForgotPassword from "pages/ForgotPassword/ForgotPassword"
import Login from "pages/Login/Login"
import AddOffer from "pages/Profile/AddOffer/AddOffer"
import ChangePassword from "pages/Profile/ChangePassword/ChangePassword"
import EditOffer from "pages/Profile/EditOffer/EditOffer"
import ExtendOffer from "pages/Profile/ExtendOffer/ExtendOffer"
import OfferInfo from "pages/Profile/Panel/OfferInfo"
import Offers from "pages/Profile/Panel/Offers"
import Profile from "pages/Profile/Panel/Profile"
import TransactionStatus from "pages/Profile/Transaction/TransactionStatus"
import Register from "pages/Register/Register"
import RegisterProfile from "pages/Register/RegisterProfile"
import { LocalStorageService } from "services/api-service"
import { RootState } from "store/store"
import Spinner from "ui/components/atoms/spinner"
import { getQuery } from "utils/queryHelper"
import AdminOfferAnalyticsModal from "views/Admin/components/modals/AdminOfferAnalyticsModal"
import AdminProfileModal from "views/Admin/components/modals/AdminProfileModal"
import OffersList from "views/Admin/Offers/OffersList"
import TslNewsletterArticleDetail from "views/Admin/TslNewsletter/ArticleDetail"
import TslNewsletterArticlesList from "views/Admin/TslNewsletter/ArticlesList"
import TslNewsletterEventsList from "views/Admin/TslNewsletter/EventsList"
import TslNewsletterSourcesList from "views/Admin/TslNewsletter/SourcesList"
import TslNewsletterSubscriberDetail from "views/Admin/TslNewsletter/SubscriberDetail"
import TslNewsletterSubscribersList from "views/Admin/TslNewsletter/SubscribersList"
import OfferAnalyticsModal from "views/Profile/components/modals/OfferAnalyticsModal"
import OfferApplicationAccountModal from "views/Profile/components/modals/OfferApplicationAccountModal"
import OfferSurveyAnswerModal from "views/Profile/components/modals/OfferSurveyAnswerModal"
import OfferApplications from "views/Profile/Offers/OfferApplications/OfferApplications"
import ProfilePersonalView from "views/Profile/Profile/ProfileAddressView"
import ProfileCompanyView from "views/Profile/Profile/ProfileCompanyView"
import ProfileKnowledgeBaseFAQsView from "views/Profile/Profile/ProfileKnowledgeBaseFAQsView"
import ProfileKnowledgeBaseScrapingsView from "views/Profile/Profile/ProfileKnowledgeBaseScrapingsView"
import ProfileKnowledgeBaseView from "views/Profile/Profile/ProfileKnowledgeBaseView"
import ProfileSettingsView from "views/Profile/Profile/ProfileSettingsView"
import ProfileSurveysView from "views/Profile/Profile/ProfileSurveysView"
import ProfileView from "views/Profile/Profile/ProfileView"

function Home() {
  const query = getQuery()
  const { isVerifying, isAuthenticated, user } = useSelector((state: RootState) => state.auth)
  const { profile } = useSelector((state: RootState) => state.profile)

  useEffect(() => {
    if (query.get("code")) {
      LocalStorageService.setObject("code", `${query.get("code")}`)
    }
  }, [query])

  if (isVerifying) {
    return (
      <InitWrapper>
        <Spinner />
      </InitWrapper>
    )
  }

  return (
    <InitWrapper>
      <BrowserRouter>
        <Analytics>
          <Routes>
            {!isAuthenticated && !user && (
              <>
                <Route path="/login" element={<Login />} />
                <Route path="/reset-password" element={<ForgotPassword />} />
                <Route path="/register" element={<Register />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </>
            )}
            {isAuthenticated && user && user.status === "create_profile" && (
              <>
                <Route path="/register-profile" element={<RegisterProfile />} />
                <Route path="*" element={<Navigate to="/register-profile" />} />
              </>
            )}
            {isAuthenticated && profile && profile.status === "add_offer" && (
              <>
                <Route path="/add-offer" element={<AddOffer />} />
                <Route path="/add-offer/status" element={<TransactionStatus />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="*" element={<Navigate to="/add-offer" />} />
              </>
            )}
            {isAuthenticated && profile && profile.status === "active" && (
              <>
                <Route path="/add-offer" element={<AddOffer />} />
                <Route
                  path="/transaction/status/:offerId/:transactionId"
                  element={<TransactionStatus />}
                />
                {/*<Route path="/edit-offer/:id" element={<EditOffer />} />*/}
                <Route path="/extend-offer/:id" element={<ExtendOffer />} />
                <Route path="/change-password" element={<ChangePassword />} />
                <Route path="panel" element={<PanelLayout />}>
                  <Route path="offers" element={<Offers />}>
                    <Route path="m/:offerId/analytics" element={<OfferAnalyticsModal />} />
                  </Route>
                  <Route path="offers/:offerId" element={<OfferInfo />}>
                    <Route path="applications" element={<OfferApplications />}>
                      <Route
                        path="account/:applicationId"
                        element={<OfferApplicationAccountModal />}
                      />
                      <Route path="survey/:applicationId" element={<OfferSurveyAnswerModal />} />
                    </Route>
                  </Route>
                  <Route path="profile" element={<Profile />}>
                    <Route path="" element={<ProfileView />} />
                    <Route path="settings" element={<ProfileSettingsView />} />
                    <Route path="surveys" element={<ProfileSurveysView />} />
                    <Route path="knowledge-base" element={<ProfileKnowledgeBaseView />}>
                      <Route path="" element={<ProfileKnowledgeBaseFAQsView />} />
                      <Route path="scrapings" element={<ProfileKnowledgeBaseScrapingsView />} />
                    </Route>
                    <Route
                      path="billing"
                      element={
                        profile?.type === "company" ? (
                          <ProfileCompanyView />
                        ) : (
                          <ProfilePersonalView />
                        )
                      }
                    />
                  </Route>
                  <Route index element={<Navigate to="offers" />} />
                </Route>
                <Route path="*" element={<Navigate to="/panel/offers" />} />
              </>
            )}
            {isAuthenticated && user?.role === "ROLE_ADMIN" && (
              <>
                {/*<Route path="/add-offer/:profileId" element={<AdminAddOffers />} />*/}
                <Route path="panel" element={<PanelLayout />}>
                  <Route path="profiles" element={<AdminProfiles />}>
                    <Route path=":profileId" element={<AdminProfileModal />} />
                  </Route>
                  <Route path="offers" element={<AdminOffers />}>
                    <Route path="" element={<OffersList />} />
                    <Route path=":offerId" element={<AdminOfferInfo />}>
                      <Route path="analytics" element={<AdminOfferAnalyticsModal />} />
                    </Route>
                  </Route>
                  <Route path="transactions" element={<AdminTransactions />} />
                  <Route path="tsl-newsletter" element={<AdminTslNewsletterDashboard />} />
                  <Route path="tsl-newsletter/sources" element={<TslNewsletterSourcesList />} />
                  <Route path="tsl-newsletter/articles" element={<TslNewsletterArticlesList />} />
                  <Route
                    path="tsl-newsletter/articles/:articleId"
                    element={<TslNewsletterArticleDetail />}
                  />
                  <Route path="tsl-newsletter/events" element={<TslNewsletterEventsList />} />
                  <Route
                    path="tsl-newsletter/subscribers"
                    element={<TslNewsletterSubscribersList />}
                  />
                  <Route
                    path="tsl-newsletter/subscribers/:subscriberId"
                    element={<TslNewsletterSubscriberDetail />}
                  />
                  <Route path="tsl-newsletter/preview" element={<AdminTslNewsletterPreview />} />
                  <Route path="tools" element={<AdminTools />} />
                </Route>
                <Route path="*" element={<Navigate to="/panel/profiles" />} />
              </>
            )}
          </Routes>
        </Analytics>
      </BrowserRouter>
    </InitWrapper>
  )
}

export default Home

const InitWrapper = styled.div`
  width: 100vw;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background: rgba(0, 0, 0, 0.8);
`
